import { defineStore } from 'pinia';
import { useContentStore } from '@/stores/content';

interface FormsEditorState {
  /** Chosen branch per box: maps box name -> selected value/intent */
  selectedPath: Record<string, any>;
  /** The form steps (child boxes) currently rendered along the selected path */
  renderedSteps: any[];
  /** Intent name of the form being edited; null when none */
  currentIntent: string | null;
  /** Box name of the form being edited; null when none */
  currentBox: string | null;
  /** Channel id of the form being edited; null when none */
  currentChannel: string | null;
  /** True if the form's completion contains a 'botform_finish' $event tracking action */
  isEventTrackingAvailable: boolean;
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/formsEditor.ts` (namespace
 * `formsEditor`). In-memory only — no `vuexPersist` option (legacy module was never listed in
 * either `vuexLocal`/`vuexSession` reducer in `src/store/index.ts`).
 *
 * Cross-namespace: `updateRenderedSteps` reads `content/getContent` via `useContentStore()`
 * (called inside the action body, per migration convention, not at module top level).
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - Legacy getter `selectedPath` collided with the state prop of the same name -> renamed
 *   `selectedPathGetter`.
 * - Legacy getter `isEventTrackingAvailable` collided with the state prop of the same name ->
 *   renamed `isEventTrackingAvailableGetter`.
 * - Legacy mutation `selectPath` + legacy action `selectPath` (same name) -> merged into a single
 *   action `selectPath`. NOTE: the legacy action's body committed a mutation type
 *   `'selectedPath'` that did not exist anywhere in this module (a latent bug — dispatching the
 *   legacy action was effectively a no-op; nothing in the codebase actually dispatched it). The
 *   only real caller (`WebhookTesting.vue`) always used `commit('formsEditor/selectPath', ...)`
 *   directly, i.e. the *mutation*, never the action. The merged action below keeps the mutation's
 *   real (working) behavior, matching actual runtime behavior rather than the dead/buggy action
 *   body.
 * - `setForm`, `setSteps`, `resetPath`, `resetState`, `setEventTracking` were mutations only ->
 *   became same-named actions, no collisions.
 */
export const useFormsEditorStore = defineStore('formsEditor', {
  state: (): FormsEditorState => ({
    selectedPath: {},
    renderedSteps: [],
    currentIntent: null,
    currentBox: null,
    currentChannel: null,
    isEventTrackingAvailable: false,
  }),
  getters: {
    formSteps: (state): any[] => state.renderedSteps,
    getSelectedPath:
      (state) =>
      (box: string): any =>
        state.selectedPath[box],
    /**
     * Renamed from legacy getter `selectedPath` (collided with the state prop of the same name;
     * the getter is renamed `<name>Getter` per convention). Identity passthrough kept for parity
     * — consumers may prefer reading `store.selectedPath` directly.
     */
    selectedPathGetter: (state): Record<string, any> => state.selectedPath,
    /**
     * Renamed from legacy getter `isEventTrackingAvailable` (collided with the state prop of the
     * same name; the getter is renamed `<name>Getter` per convention).
     */
    isEventTrackingAvailableGetter: (state): boolean => state.isEventTrackingAvailable,
  },
  actions: {
    /**
     * Recomputes `renderedSteps` (and the event-tracking flag) by crawling the form's box tree
     * returned by `content/getContent`, following the box graph via `next`/`values` plus the
     * currently selected path per box.
     */
    updateRenderedSteps(currentBotId: string) {
      const contentStore = useContentStore();
      // `content/getContent`'s getter type only declares 3 params (botId, intent, box); the
      // legacy call passed 2 extra args (`true`, channel) that the underlying function has always
      // ignored at runtime (JS drops extra call args). Cast to `any` to keep passing all 5 args
      // exactly as legacy did, without narrowing/altering the call.
      let content = (contentStore.getContent as any)(
        currentBotId,
        this.currentIntent,
        this.currentBox,
        true,
        // pre-existing quirk kept from the legacy Vuex action: the original code read
        // `(this as any).currentChannel`, where `this` was bound to the root Vuex store (not this
        // module's own state), so this argument was effectively always `undefined` at runtime.
        // Under Pinia, `this` correctly refers to this store's own state, so `this.currentChannel`
        // now resolves as originally intended. Not treated as a consumer-facing rename/contract
        // change.
        this.currentChannel,
      );
      let renderedSteps: any[] = [];

      if (!content || !content.childBoxes || content.childBoxes.length === 0) {
        this.setSteps({ steps: renderedSteps });
        return;
      }

      // find event tracking in completion
      if (content.completion && content.completion.actions && content.completion.actions.length > 0) {
        let eventTracking = content.completion.actions.find(
          (action: any) => action.name === '$event'
            && action.parameters && action.parameters.name === 'botform_finish',
        );
        this.setEventTracking({ isAvailable: !!eventTracking });
      }

      // Converted to an arrow function (legacy module used a nested `function crawl`) so it
      // captures the store's `this` lexically for the `this.selectPath(...)` call below — a plain
      // nested `function` declaration would lose the store binding for `this`.
      const crawl = (boxes: any[], next: string): any[] => {
        let box = content.childBoxes.find((b: any) => {
          return b.box === next;
        });

        if (box) {
          boxes.push(box);

          if (Array.isArray(box.values) && box.values.length > 0) {
            if (this.selectedPath[box.box]) {
              let value = box.values.find((v: any) => {
                return v.value === this.selectedPath[box.box];
              });
              if (value) {
                return crawl(boxes, value.box);
              } else {
                return boxes;
              }
            } else {
              this.selectPath({ box: box.box, intent: box.values[0].value });
              return crawl(boxes, box.values[0].box);
            }
          } else {
            return crawl(boxes, box.next);
          }
        }

        return boxes;
      };

      renderedSteps = crawl([], content.start.next);

      this.setSteps({ steps: renderedSteps });
    },
    /**
     * Merged from legacy mutation `selectPath` + legacy action `selectPath` (see class-level
     * doc for the dead/buggy legacy action body this replaces). Records the selected
     * value/intent for a given form box.
     */
    selectPath(payload: { box: string; intent: any }) {
      if (payload.box && payload.intent) {
        this.selectedPath = { ...this.selectedPath, [payload.box]: payload.intent };
      }
    },
    /**
     * Converted from legacy mutation `setForm` (no matching legacy action existed). Sets which
     * intent/box/channel is currently being edited.
     */
    setForm(payload: { intent: string | null; box: string | null; channel: string | null }) {
      this.currentIntent = payload.intent;
      this.currentBox = payload.box;
      this.currentChannel = payload.channel;
    },
    /**
     * Converted from legacy mutation `setSteps` (no matching legacy action existed).
     */
    setSteps(payload: { steps: any[] }) {
      this.renderedSteps = payload.steps;
    },
    /**
     * Converted from legacy mutation `resetPath` (no matching legacy action existed).
     */
    resetPath() {
      this.selectedPath = {};
    },
    /**
     * Converted from legacy mutation `resetState` (no matching legacy action existed).
     */
    resetState() {
      this.selectedPath = {};
      this.renderedSteps = [];
      this.currentIntent = null;
      this.currentBox = null;
      this.currentChannel = null;
    },
    /**
     * Converted from legacy mutation `setEventTracking` (no matching legacy action existed).
     */
    setEventTracking(payload: { isAvailable: boolean }) {
      this.isEventTrackingAvailable = payload.isAvailable;
    },
  },
});
